import React, { useEffect, useRef, useState, useCallback } from 'react';
import AddOrderSupplierModal from './AddOrderSupplierModal';
import { MdClose } from 'react-icons/md';
import { InfinitySpin } from 'react-loader-spinner';
import { FcSearch } from 'react-icons/fc';
import { useSelector } from 'react-redux';
import { api_url } from '../../../config';
import api from '../../../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ChooseSupplier = ({ onSelectSupplier }) => {
    const [externalSuppliers, setExternalSuppliers] = useState([]);
    const [internalSuppliers, setInternalSuppliers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [activeTab, setActiveTab] = useState('added');
    const [metadata, setMetadata] = useState('');
    const [search, setSearch] = useState('');
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState();
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleNextPage = () => {
        if (currentPage < lastPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    function getPageNumbers() {
        const pageNumbers = [];
        const totalDisplayed = 7;
        const sideButtons = 2;

        if (lastPage <= totalDisplayed) {
            for (let i = 1; i <= lastPage; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);

            if (currentPage > sideButtons + 2) {
                pageNumbers.push('...');
            }

            let rangeStart = Math.max(2, currentPage - sideButtons);
            let rangeEnd = Math.min(lastPage - 1, currentPage + sideButtons);

            if (currentPage <= sideButtons + 2) {
                rangeEnd = totalDisplayed - 2;
            } else if (currentPage >= lastPage - sideButtons - 1) {
                rangeStart = lastPage - totalDisplayed + 3;
            }

            for (let i = rangeStart; i <= rangeEnd; i++) {
                pageNumbers.push(i);
            }

            if (currentPage < lastPage - sideButtons - 1) {
                pageNumbers.push('...');
            }

            pageNumbers.push(lastPage);
        }

        return pageNumbers;
    }

    const modalRef = useRef();
    const { id, branch_id, token } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    const fetchExternalSuppliers = useCallback(async (page, limit, searchQuery) => {
        setLoading(true);
        if (!navigator.onLine) {
            toast("You're offline");
            setLoading(false);
            return;
        }
        const params = {
            page: page || 1,
            limit: limit || 50,
            search: `${searchQuery || ''}`,
            'filter[on_pharmtrix]': '0',
            'filter[status]': 'active,inactive,archived'
        };
        try {
            const response = await api.get(api_url + `/api/v1/retailer/user/${id}/branch/${branch_id}/contact/inbranch`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                params
            });
            const data = response.data.data;
            const meta = response.data.meta;
            setExternalSuppliers(data);
            setSuppliers(data);
            setMetadata(meta);
            setLastPage(meta.last_page);
        } catch (error) {
            console.error('Error fetching external suppliers:', error);
            toast.error(error?.response?.data?.error || 'Failed to fetch external suppliers');
            setExternalSuppliers([]);
            setSuppliers([]);
        } finally {
            setLoading(false);
        }
    }, [id, branch_id, token]);

    const fetchInternalSuppliers = useCallback(async (page, limit, searchQuery) => {
        setLoading(true);
        if (!navigator.onLine) {
            toast("You're offline");
            setLoading(false);
            return;
        }
        const params = {
            page: page || 1,
            limit: limit || 50,
            search: `${searchQuery || ''}`
        };
        try {
            const response = await api.get(api_url + `/api/v1/retailer/user/${id}/branch/${branch_id}/supplier/internal`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                params
            });
            const data = response.data.data;
            const meta = response.data.meta;
            setInternalSuppliers(data);
            setSuppliers(data);
            setMetadata(meta);
            setLastPage(meta.last_page);
        } catch (error) {
            console.error('Error fetching internal suppliers:', error);
            toast.error('Failed to fetch internal suppliers');
        } finally {
            setLoading(false);
        }
    }, [id, branch_id, token]);

    useEffect(() => {
        if (activeTab === 'added') {
            fetchInternalSuppliers(currentPage, limit);
        } else if (activeTab === 'not-added') {
            fetchExternalSuppliers(currentPage, limit);
        }
    }, [activeTab, currentPage, fetchExternalSuppliers, fetchInternalSuppliers, limit]);

    useEffect(() => {
        if (search.trim() === '') {
            setFilteredSuppliers(suppliers);
        } else {
            const filtered = suppliers.filter(supplier =>
                supplier.name?.toLowerCase().includes(search.toLowerCase()) ||
                supplier.address?.toLowerCase().includes(search.toLowerCase()) ||
                supplier.phone?.toLowerCase().includes(search.toLowerCase()) ||
                supplier.email?.toLowerCase().includes(search.toLowerCase())
            );
            setFilteredSuppliers(filtered);
        }
    }, [search, suppliers]);

    const addInternalSupplier = async (data) => {
        if (!data) {
            toast.error('Invalid supplier data');
            return;
        }

        if (!data.on_pharmtrix) {
            toast.error('This supplier is not registered on pharmtrix. Please ensure the supplier is registered first.');
            return;
        }

        const supplierDetails = {
            name: data.name || '',
            city: data.city || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            town: data.town || '',
            ghana_post: data.ghana_post || '',
            on_pharmtrix: true
        };

        try {
            setLoading(true);
            await api.post(api_url + `/api/v1/retailer/user/${id}/branch/${branch_id}/contact/${data.id}/internal`, supplierDetails, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });

            await Promise.all([
                fetchExternalSuppliers(currentPage, limit),
                fetchInternalSuppliers(currentPage, limit)
            ]);

            toast.success(`Successfully added ${data.name} to your internal suppliers list. You can now create orders with this supplier.`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            setShowAddSupplierModal(false);
            setSelectedSupplier(null);

        } catch (error) {
            console.error('Error adding supplier:', error);
            if (error.response?.status === 403) {
                toast.error('You do not have permission to add this supplier. Please contact your administrator.');
            } else if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error('Failed to add supplier. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const switchTab = (tab) => {
        setLoading(true);
        setActiveTab(tab);
        setSuppliers([]);
        setExternalSuppliers([]);
        setInternalSuppliers([]);
        setCurrentPage(1);
    };

    const externalSupplierToAdd = (supplier) => {
        if (!supplier.on_pharmtrix) {
            toast.error("Oops! It looks like this supplier isn’t subscribed to Pharmtrix yet. Please contact them to activate their account before placing an order.");
            return;
        }
        setShowAddSupplierModal(false);
        setSelectedSupplier(supplier);
    };

    return (
        <div className=''>
            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <InfinitySpin color="#059212" loading={loading} size={40} />
                </div>
            )}
            <div className='flex flex-col items-center justify-between mb-6 sm:flex-row'>
                <div className='flex mb-3 overflow-auto xl:gap-10 font-lufga'>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-6 relative transition-all duration-200 ${activeTab === 'added'
                            ? 'text-green-600 font-medium after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-500'
                            : 'text-gray-600 hover:text-green-600'
                            }`}
                        onClick={() => switchTab('added')}
                    >
                        Pharmtrix Suppliers
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-6 relative transition-all duration-200 ${activeTab === 'not-added'
                            ? 'text-green-600 font-medium after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-500'
                            : 'text-gray-600 hover:text-green-600'
                            }`}
                        onClick={() => switchTab('not-added')}
                    >
                        Non-Pharmtrix Suppliers
                    </button>
                </div>
                <div className='flex items-center gap-2'>
                    <div className="relative">
                        <input
                            type='text'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder='Search suppliers...'
                            className='w-38 sm:w-[250px] focus:outline-none rounded-md shadow-sm border px-4 py-2.5 h-10 font-lufga text-sm pl-10'
                        />
                        <FcSearch className='absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2' />
                    </div>
                </div>
            </div>

            {filteredSuppliers?.length > 0 ? (
                <div className='w-full flex flex-wrap gap-4 px-[2px] justify-center sm:justify-normal items-center py-2'>
                    {filteredSuppliers?.map(supplier => (
                        <div
                            key={supplier.id}
                            className="flex items-start justify-center"
                        >
                            <div
                                className={`bg-gradient-to-br ${activeTab === 'added' ? 'from-green-100 to-green-300 border-[1px] text-green-900 border-green-500' : 'from-gray-100 to-gray-300 border-[1px] text-gray-900 border-gray-500'} w-[280px] hover:scale-105 duration-150 cursor-pointer ease-in max-h-min rounded-lg shadow-lg p-4 flex flex-col gap-3`}
                                onClick={() => activeTab === 'added' ? onSelectSupplier(supplier) : externalSupplierToAdd(supplier)}
                            >
                                <div className='flex flex-col gap-2'>
                                    <div className="flex items-center justify-between">
                                        <h3 className='text-base font-semibold basis-[40%] truncate hover:text-clip font-lufga'>{supplier?.name}</h3>
                                        <span className={`px-2 py-2 text-xs  basis-[45%] rounded-full ${activeTab === 'added' ? 'bg-green-200 text-green-800' : 'bg-gray-600 text-black'}`}>
                                            {activeTab === 'added' ? 'Pharmtrix' : 'Non-Pharmtrix'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <p className='text-xs w-[200px] truncate hover:text-clip'>{supplier?.address}</p>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <p className='text-xs w-[200px] truncate hover:text-clip'>{supplier?.phone}</p>
                                    </div>
                                    {supplier?.email && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            <p className='text-xs w-[200px] truncate hover:text-clip'>{supplier?.email}</p>
                                        </div>
                                    )}
                                </div>
                                <div className='flex justify-end mt-2'>
                                    {activeTab === 'not-added' && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                externalSupplierToAdd(supplier);
                                            }}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-white transition-colors duration-200 bg-green-500 rounded-md shadow-sm hover:bg-green-600"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Create Supplier
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                    <div className="w-24 h-24 mb-4 text-gray-400">
                        {!navigator.onLine ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        )}
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        {!navigator.onLine ? "No Internet Connection" : "No Suppliers Found"}
                    </h3>
                    <p className="max-w-sm mb-6 text-gray-600">
                        {!navigator.onLine
                            ? "Please check your internet connection and try again."
                            : activeTab === 'added'
                                ? "You haven't added any Pharmtrix suppliers yet. Add suppliers to start creating orders."
                                : "No non-Pharmtrix suppliers found. You can add new suppliers to your list."}
                    </p>
                    {!navigator.onLine ? (
                        <button
                            onClick={() => window.location.reload()}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-white transition-colors duration-200 bg-blue-500 rounded-md hover:bg-blue-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retry Connection
                        </button>
                    ) : (
                        <button
                            onClick={() => navigate('/suppliers')}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-white transition-colors duration-200 bg-green-500 rounded-md hover:bg-green-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Go to Suppliers
                        </button>
                    )}
                </div>
            )}

            {showAddSupplierModal && selectedSupplier && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
                    <div ref={modalRef} className="p-6 bg-white rounded-md shadow-lg min-h-max">
                        <MdClose
                            size={24}
                            onClick={() => {
                                setShowAddSupplierModal(false);
                                setSelectedSupplier(null);
                            }}
                            className='transition duration-150 ease-out cursor-pointer hover:scale-105 hover:text-red-800'
                        />
                        <AddOrderSupplierModal
                            toggle={() => {
                                setShowAddSupplierModal(false);
                                setSelectedSupplier(null);
                            }}
                            addInternalSupplier={addInternalSupplier}
                            selectedSupplier={selectedSupplier}
                        />
                    </div>
                </div>
            )}
            <div className='flex items-center gap-4 mt-4'>
                <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={currentPage === 1 ? 'text-gray-500 text-xs' : 'text-xs text-green-600'}
                >
                    Previous
                </button>
                <div className='flex items-center gap-3'>
                    {getPageNumbers().map((pageNumber, index) => (
                        <React.Fragment key={index}>
                            {pageNumber === '...' ? (
                                <span className="text-xs">...</span>
                            ) : (
                                <button
                                    onClick={() => handlePageClick(pageNumber)}
                                    className={`text-xs ${currentPage === pageNumber ? 'font-bold' : ''}`}
                                >
                                    {pageNumber}
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                <button
                    onClick={handleNextPage}
                    disabled={currentPage === lastPage}
                    className={currentPage === lastPage ? 'text-gray-500 text-xs' : 'text-xs text-green-600'}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default ChooseSupplier;
